<script setup lang="ts">
import { useChapterHeadings } from "../composables/useChapterHeadings";
/**
 * Right-side counterpart to the outline — mounted only on routes that declare
 * `EditorContextPane` as the `context` named view (storyboard + database entry).
 */
import { computed } from "vue";
import { isJust, matchMaybe } from "../lib/maybe";
import { useRoute } from "vue-router";
import { displayFileName } from "../lib/fileNames";
import { chapterFolderFromScenePath } from "../lib/zwConfig";
import { useEditorHeadingsStore } from "../stores/useEditorHeadingsStore";
import { useEditorSessionStore } from "../stores/useEditorSessionStore";
import { useProjectWorkspaceStore } from "../stores/useProjectWorkspaceStore";
import ContextPanel from "./context/ContextPanel.vue";
import EntityLinksPanel from "./EntityLinksPanel.vue";
import EntityScenesPanel from "./EntityScenesPanel.vue";
import { useActiveEntityFieldsStore } from "../stores/useActiveEntityFieldsStore";
import EntityArcPanel from "./EntityArcPanel.vue";
import StoryboardUnmappedMomentsPanel from "./storyboard/StoryboardUnmappedMomentsPanel.vue";

const { chapterLabel } = useChapterHeadings();
const entityFields = useActiveEntityFieldsStore();
const isCharacter = computed(
  () =>
    isJust(entityFields.fields.type) &&
    entityFields.fields.type.value === "character",
);
const session = useEditorSessionStore();
const workspace = useProjectWorkspaceStore();
const headings = useEditorHeadingsStore();
const route = useRoute();

const showStoryboardContext = computed(() => route.name === "storyboard");
const showDatabaseContext = computed(() => route.name === "database-entry");
const hasContent = computed(
  () => showDatabaseContext.value || showStoryboardContext.value,
);

const occurrences = computed(() => headings.entityOccurrences ?? []);

/** Display title for a chapter row — the chapter folder name, not the scene filename. */
function chapterDisplayName(path: string): string {
  return matchMaybe(chapterFolderFromScenePath(path), {
    just: (folder) => chapterLabel(folder),
    nothing: () => displayFileName(path),
  });
}

function closeMobileContextPanel(): void {
  if (session.mobileShell) {
    session.contextPanelOpen = false;
  }
}

function onJump(file: string, from: number): void {
  void workspace.onJumpInFile(file, from);
  closeMobileContextPanel();
}
</script>

<template>
  <ContextPanel
    v-if="hasContent"
    :title="showStoryboardContext ? 'Scene links' : 'Entry context'"
    :body-class="showStoryboardContext ? 'zw-context-body-storyboard' : ''"
  >
    <StoryboardUnmappedMomentsPanel v-if="showStoryboardContext" />
    <template v-else>
      <EntityLinksPanel />
      <EntityArcPanel
        v-if="isCharacter || entityFields.fields.arc.length > 0"
      />
      <EntityScenesPanel v-if="isCharacter === false" />

      <section class="zw-context-occurrences">
        <h3>Occurrences</h3>
        <p v-if="occurrences.length === 0" class="zw-context-empty">
          No matches in chapters yet.
        </p>
        <ul v-else class="occurrence-list">
          <li
            v-for="(hit, index) in occurrences"
            :key="`${hit.file}-${hit.from}-${index}`"
          >
            <button
              type="button"
              class="occurrence-link"
              :title="`${chapterDisplayName(hit.file)} · ${hit.unit} ${hit.lineNumber}`"
              @click="onJump(hit.file, hit.from)"
            >
              <span class="occurrence-meta">
                {{ chapterDisplayName(hit.file) }}
                <span aria-hidden="true">·</span>
                {{ hit.unit === "paragraph" ? "¶" : "L" }}{{ hit.lineNumber }}
              </span>
              <span class="occurrence-snippet">{{ hit.snippet }}</span>
            </button>
          </li>
        </ul>
      </section>
    </template>
  </ContextPanel>
</template>

<style scoped>
:deep(.zw-context-body-storyboard) {
  display: flex;
  flex-direction: column;
}

.zw-context-empty {
  margin: 0 0.35rem;
  font-size: 0.85rem;
  line-height: 1.4;
  color: color-mix(in oklch, var(--text-muted) 85%, transparent);
}

.zw-context-occurrences {
  margin-top: var(--space-md);
  padding-top: var(--space-md);
  border-top: 1px solid var(--line);
}

.zw-context-occurrences h3 {
  margin: 0 0 0.35rem;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
}

.occurrence-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.occurrence-link {
  appearance: none;
  border: 0;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.1rem;
  width: 100%;
  padding: 0.35rem 0.45rem;
  text-align: left;
  font: inherit;
  cursor: pointer;
  border-radius: 0.25rem;
}

.occurrence-link:hover {
  background: var(--hover-fill);
}

.occurrence-meta {
  font-size: 0.78rem;
  color: var(--text-muted);
}

.occurrence-snippet {
  font-size: 0.88rem;
  line-height: 1.35;
  color: var(--text);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
